import * as React from "react";
import {Button, Col, Row, Table, FormGroup, FormLabel, FormControl, Spinner} from "react-bootstrap";
import { SimpleCheckbox } from "Components/Common/SimpleCheckbox";
import DatePicker from "react-datepicker";
import {ScheduledIngestion} from "Stores/superStore/schools";
import {
    CellContext,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    Row as RowInfo,
    useReactTable
} from "@tanstack/react-table";
import {CrudModal} from "Components/Common/CrudModal";
import {ActionWrapper} from "Components/Common/ActionWrapper";
import {Utils} from "../../../../common/utils";


interface ScheduledIngestionsProps {
    scheduledIngestions: ScheduledIngestion[]
    onDeleteScheduledIngestion: (scheduledIngestion: ScheduledIngestion) => void
    onSaveScheduledIngestion: (ingestDate: Date | null, comment: string, fullIngestion: boolean) => void
    onUpdateScheduledIngestion: (id: number, ingestDate: Date | null, comment: string, fullIngestion: boolean) => void
    isSaving: boolean
}

interface ScheduledIngestionsState {
    showModal: boolean
    isEdit: boolean
    selectedScheduledIngestionId: number
    ingestDate: Date | null
    fullIngestion: boolean
    comment: string
}

export function ScheduledIngestions(props: ScheduledIngestionsProps) {
    const [state, setState] = React.useState<ScheduledIngestionsState>({
        showModal: false,
        isEdit: false,
        selectedScheduledIngestionId: 0,
        ingestDate: null,
        fullIngestion: false,
        comment: ""
    });

    const columns: ColumnDef<ScheduledIngestion>[] = [
        {
            header: "Status",
            accessorKey: "status",
            minSize: 80,
            maxSize: 100,
        },
        {
            header: "Ingest Date",
            id: "ingest_date",
            accessorFn: (d: ScheduledIngestion) => d.ingest_date ? Utils.formatDate(new Date(d.ingest_date), "MM/DD/YYYY") : undefined,
            minSize: 80,
            maxSize: 100,
        },
        {
            header: "Comment",
            accessorKey: "comment",
        },
        {
            header: "Created By",
            accessorKey: "created_by_name",
            minSize: 120,
            maxSize: 140,
        },
        {
            header: "Full Ingestion",
            accessorKey: "full_ingestion",
            cell: (props: CellContext<ScheduledIngestion, unknown>) => (
                <div>
                    {props.row.original.full_ingestion ?
                        <i className="icon icon-check topaz-icon" aria-hidden="true"/> : null}
                </div>
            ),
            minSize: 100,
            maxSize: 120,
        },
        {
            header: "Reason",
            accessorKey: "reason",
            minSize: 250,
            maxSize: 300,
        }
    ];

    const handleStartDateChange = (ingestDate: Date | null) => {
        setState(prevState => ({...prevState, ingestDate}));
    };

    const handleFullIngestionChange = (fullIngestion: boolean) => {
        setState(prevState => ({...prevState, fullIngestion}));
    };

    const handleCommentChange = (event: any) => {
        setState(prevState => ({...prevState, comment: event.target.value}));
    };

    const handleCreateClick = () => {
        setState(prevState => ({
            ...prevState,
            showModal: true,
            isEdit: false,
        }));
    };

    const handleSave = () => {
        if (!state.ingestDate || !state.comment) {
            return;
        }
        if (state.isEdit) {
            props.onUpdateScheduledIngestion(
                state.selectedScheduledIngestionId,
                state.ingestDate,
                state.comment,
                state.fullIngestion
            );
        } else {
            props.onSaveScheduledIngestion(
                state.ingestDate,
                state.comment,
                state.fullIngestion
            );
        }
        setState({
            showModal: false,
            isEdit: false,
            selectedScheduledIngestionId: 0,
            ingestDate: null,
            fullIngestion: false,
            comment: ""
        });
    };

    const handleModalClose = () => {
        setState(prevState => ({
            ...prevState,
            showModal: false,
            isEdit: false,
        }));
    };

    const handleEdit = (scheduledIngestion: ScheduledIngestion) => {
        setState({
            showModal: true,
            isEdit: true,
            selectedScheduledIngestionId: scheduledIngestion.id,
            ingestDate: new Date(scheduledIngestion.ingest_date),
            fullIngestion: scheduledIngestion.full_ingestion,
            comment: scheduledIngestion.comment
        });
    };

    const handleDelete = (scheduledIngestion: ScheduledIngestion) => {
        props.onDeleteScheduledIngestion(scheduledIngestion);
    };

    const renderForm = () => {
        return (
            <fieldset className="fieldset">
                <div className="inner">
                    <Row>
                        <Col>
                            <label>Scheduled Ingestion</label>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <label>Ingest Date</label>
                            <div>
                                <DatePicker
                                    id="ingestion-start-date"
                                    placeholderText="Select a date..."
                                    autoComplete="off"
                                    selected={state.ingestDate}
                                    onChange={handleStartDateChange}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup controlId="user-form-prefix">
                                <FormLabel>Comment:</FormLabel>
                                <FormControl
                                    type="input"
                                    value={state.comment}
                                    onChange={handleCommentChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <SimpleCheckbox
                                label="Full Ingestion"
                                isChecked={state.fullIngestion}
                                id="full_ingestion"
                                onChange={handleFullIngestionChange}
                            />
                        </Col>
                    </Row>
                </div>
            </fieldset>
        );
    };

    const isFormIncomplete = () => {
        return !state.ingestDate || !state.comment;
    };

    const renderActionButton = (obj: ScheduledIngestion) => {
        const actionProps = {
            data: obj,
            onEdit: handleEdit,
            onDelete: handleDelete,
        };
        return (<ActionWrapper {...actionProps} />);
    };

    const customColumns: ColumnDef<ScheduledIngestion>[] = [...columns, {
        header: "Actions",
        cell: ({row}: { row: RowInfo<ScheduledIngestion> }) => renderActionButton(row.original)
    }];

    return (
        <React.Fragment>
            <Row className="button-bar form-buttons">
                <Col className="text-right">
                    <div className="actions">
                        <Button
                            onClick={handleCreateClick}
                            className="add-scheduled-ingestions-btn">
                            add scheduled ingestion
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row className={"list"}>
                {props.isSaving
                    ? <Col md={1} lg={1}><Spinner animation="border"/></Col>
                    : <ScheduledIngestionsReactTable data={props.scheduledIngestions} columns={customColumns}/>}
            </Row>
            {state.showModal
                ? <CrudModal
                    typeLabel={"Scheduled Ingestion"}
                    isEdit={state.isEdit}
                    renderForm={renderForm}
                    onClose={handleModalClose}
                    onSave={handleSave}
                    isDisabled={isFormIncomplete()}
                    showSpinner={props.isSaving}/>
                : null}
        </React.Fragment>
    );
}

interface ScheduledIngestionsReactTableProps {
    data: ScheduledIngestion[]
    columns: ColumnDef<ScheduledIngestion>[]
}

function ScheduledIngestionsReactTable({columns, data}: ScheduledIngestionsReactTableProps) {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <Table>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                        <th key={header.id}>
                            {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                        </th>
                    ))}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (<tr><td colSpan={7} className="text-center">No Scheduled Ingestions</td></tr>)}
            </tbody>
        </Table>
    );
}